import type { SortingState } from '@tanstack/react-table'
import type { BoardSort } from './useTreatmentBoard'

export type BandSort = { sort: BoardSort; direction: `ASC` | `DESC` }

/**
 * The two columns the band query can actually order by, and the sort each one
 * asks for.
 *
 * Every other column sorts nothing. A band holds thousands of cases and the
 * table reads a page at a time, so a header that reordered the rows in hand
 * would claim to have sorted a list it has never seen.
 */
export const TREATMENT_SORT_COLUMNS: Record<string, BoardSort> = {
  Unscheduled: `value`,
  Planned: `presented`,
}

/** Which header wears the arrow for a given band sort. The inverse of the map above. */
export const SORT_COLUMN: Record<BoardSort, string> = { value: `Unscheduled`, presented: `Planned` }

/**
 * The band sort a click on a column header lands on.
 *
 * The table shell's own toggle runs a three-step cycle (unsorted, ascending,
 * descending) and hands back the state it arrived at. The band query has no
 * unsorted state to offer, so this reads that third step as "turn the column
 * around" instead, leaving each column a plain two-way toggle.
 *
 * A column being sorted for the first time opens descending whatever the
 * shell proposed: the largest cases and the most recently planned are what
 * anybody reaching for those two headers is after, and making them ask twice
 * for it is a click spent on nothing.
 */
export function nextSort(current: BandSort, state: SortingState): BandSort {
  const clicked = state[0]
  if (!clicked) return { sort: current.sort, direction: current.direction === `DESC` ? `ASC` : `DESC` }
  const sort = TREATMENT_SORT_COLUMNS[clicked.id]
  if (!sort) return current
  if (sort !== current.sort) return { sort, direction: `DESC` }
  return { sort, direction: clicked.desc ? `DESC` : `ASC` }
}

/** The two readings of a treatment band. */
export type TreatmentView = `board` | `table`

/**
 * The sort the band query actually runs, for the view that is on screen.
 *
 * The two views share a field, so flipping between them does not reshuffle the
 * queue under you. They deliberately do not share a direction. Only the
 * table's column headers can turn a sort around; the board's picker offers
 * `Recently presented` and `Largest first` and nothing that shows or changes
 * which end comes first. Left shared, sorting a table column ascending would
 * silently reorder the board into an order no control on it admits to, and the
 * only way back would be to return to the table and sort it again.
 *
 * The table keeps its own direction while it is away, so coming back to it
 * lands on the order you left it in.
 */
export function bandSortFor(view: TreatmentView, sort: BandSort): BandSort {
  return view === `board` ? { sort: sort.sort, direction: `DESC` } : sort
}
